import type { UseDeleteOptions } from "./useDelete.type";
import type { ApiEmptyResponse, ApiError } from "@/services/api";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import get from "lodash/get";

import { client } from "@/config/client";

export const useDelete = <Variables = unknown>(
  model: string,
  id: number,
  options?: UseDeleteOptions<Variables>
) => {
  const queryClient = useQueryClient();

  const {
    rootId,
    invalidateKeys,
    onSuccess,
    predicateRemoveKeys,
    mutationOptions,
  } = options ?? {};

  const mutationFn = () => {
    const destroy = get(client, [model, "destroy"]);

    if (rootId) {
      return destroy(rootId, id);
    }
    return destroy(id);
  };

  const onDeleteSuccess = async () => {
    onSuccess?.();

    await queryClient.cancelQueries();

    queryClient.removeQueries({
      predicate: ({ queryKey }) => {
        return queryKey.includes(id) && queryKey.includes(model);
      },
    });

    if (predicateRemoveKeys) {
      queryClient.removeQueries({ predicate: predicateRemoveKeys });
    }

    queryClient.invalidateQueries({ queryKey: [model] });
    invalidateKeys?.forEach((queryKey) =>
      queryClient.invalidateQueries({ queryKey })
    );
  };

  return useMutation<ApiEmptyResponse, ApiError, Variables>({
    ...mutationOptions,
    mutationFn,
    onSuccess: onDeleteSuccess,
  });
};
